import { Checkbox, Link as FluentLink, makeStyles, tokens } from '@fluentui/react-components';
import type { CheckboxOnChangeData } from '@fluentui/react-components';
import { ArrowRight16Regular } from '@fluentui/react-icons';

import { Link as ReactRouterLink } from 'react-router-dom';

import { Path } from '@shared/components';

import { EFolderType } from '@modules/items';
import { AccessType } from '@modules/access';
import type { TFolder, TItem, TItemType, TPath, TRootItems } from '@modules/items';
import { useContext, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { SignInContext } from '@modules/users';
import { isTFolder } from '../../../types/folders.types';
import { TNavigatableFolderType } from '../../../types/dialogs.types';
import { isNavigatableFolderType } from '../../../helpers/items.helper';
import SelectItemRows from './SelectItemRows';
import { buildSelectableDialogItems } from './selectItemDialogItems';

// Define styles for table and other elements.
const useStyles = makeStyles({
  selectAllCheckboxContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '6px', // Align with the radio buttons in table rows
    borderBottom: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralStroke2}`, // Thick border to distinguish from table rows

    '& .fui-Checkbox__label': {
      marginLeft: '11px', // Align with the item/folder icons in table rows
    },
  },
});

type TProps = {
  data: TFolder | TRootItems;
  setFolderId: (folderId: string) => void;
  localSelectedItems: ReadonlyMap<string, TItem>;
  setLocalSelectedItems: Dispatch<SetStateAction<ReadonlyMap<string, TItem>>>;
  checkManageAccess: boolean;
  rootFolderId?: string;
  navigatableFolderTypes: TNavigatableFolderType[];
  selectableTypes: TItemType[];
  isSelectable?: (parent: TFolder | TRootItems, item: TItem) => boolean;
  multiSelect?: boolean;
  getDisabledReason?: (item: TItem) => string | undefined;
  showNewLink?: boolean;
};

const SelectItemContent = ({
  data,
  setFolderId,
  localSelectedItems,
  setLocalSelectedItems,
  checkManageAccess,
  rootFolderId,
  navigatableFolderTypes,
  selectableTypes,
  isSelectable,
  multiSelect = false,
  getDisabledReason,
  showNewLink = true,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const isFolderSelectable = selectableTypes.includes('FOLDER');

  const pathItems: TPath = [{ _id: '', name: organization.name, accessRight: AccessType.PARTIAL }];
  const isFolder = isTFolder(data);
  if (isFolder) pathItems.push(...data.resolvedPath, { _id: data._id, name: data.name, accessRight: data.accessRight });

  // limit path:
  if (rootFolderId) {
    const i = pathItems.findIndex((i) => i._id === rootFolderId);
    if (i !== -1) pathItems.splice(i);
    else console.warn(`Root folder with ID ${rootFolderId} not found in path items.`);
  }

  // Filter folder for navigation, filter items for selection:
  const items = useMemo(() => {
    const children = data.children || [];
    return buildSelectableDialogItems({
      parent: data,
      items: children.filter((item: TItem) => isNavigatableFolderType(data, item, navigatableFolderTypes)),
      selectableTypes,
      checkManageAccess,
      isSelectable,
      getDisabledReason,
    }).sort(
      (itemA, itemB) =>
        (itemB._id === organization.folders.qm ? 1 : 0) - (itemA._id === organization.folders.qm ? 1 : 0)
    );
  }, [
    checkManageAccess,
    data,
    getDisabledReason,
    isSelectable,
    navigatableFolderTypes,
    organization.folders.qm,
    selectableTypes,
  ]);
  const selectableItems = useMemo(() => items.filter((item) => item.isSelectable), [items]);

  // Handler for the "Select All" checkbox.
  const handleSelectAllChange = ({ checked }: CheckboxOnChangeData) => {
    const newSelected = new Map(localSelectedItems);
    // Add all selectable items to the selection.
    selectableItems.forEach((item) => {
      if (checked === true) {
        newSelected.set(item._id, item);
      } else {
        newSelected.delete(item._id);
      }
    });
    setLocalSelectedItems(newSelected);
  };

  const isSelectAllChecked = useMemo(() => {
    // Check if all selectable items are selected.
    return selectableItems.length > 0 && selectableItems.every((item) => localSelectedItems.has(item._id));
  }, [selectableItems, localSelectedItems]);

  const isBlockFolderType = (data as TFolder).folderType === EFolderType.BLOCK;

  const classes = useStyles();

  return (
    <div>
      <div className="mb-3">
        <Path
          items={useFolderPathAsBreadcrumbs(pathItems)}
          hasCurrent
          onClick={(idx) => setFolderId(pathItems[idx]._id)}
        />
      </div>
      {showNewLink && isBlockFolderType && (
        <div className="mb-2 ml-2">
          <ReactRouterLink to={`/folder/${pathItems[pathItems.length - 1]._id}`} target="_blank">
            <FluentLink>
              New <ArrowRight16Regular />
            </FluentLink>
          </ReactRouterLink>
        </div>
      )}
      {/* For multiSelect: render header with a "Select All" checkbox */}
      {multiSelect && (
        <div className={classes.selectAllCheckboxContainer}>
          {/* className="flex items-center px-2 border-b"> */}
          <Checkbox
            label="Select all items"
            checked={isSelectAllChecked}
            onChange={(_event, checkboxData) => handleSelectAllChange(checkboxData)}
          />
        </div>
      )}
      <SelectItemRows
        items={items}
        selectableItems={selectableItems}
        localSelectedItems={localSelectedItems}
        setLocalSelectedItems={setLocalSelectedItems}
        isFolderSelectable={isFolderSelectable}
        multiSelect={multiSelect}
        onNavigateFolder={setFolderId}
      />
    </div>
  );
};

export default SelectItemContent;
